/**
 * 离线同步队列（PRD §10.2）。
 *
 * 队列存储在 IndexedDB 的 `syncOperations` store 中。`enqueue` 写入一条
 * 待同步操作；`process` 顺序执行 `pending` 操作（按 createdAt 排序），
 * 失败自动重试 3 次；`status` 返回当前队列计数。
 */

import { nowIso } from '../format';
import {
  listSyncOperations,
  removeSyncOperation,
  saveSyncOperation,
} from '../storage/syncOperation';
import type { SyncOperation, SyncOperationStatus } from '../../model/types';

/** 最大重试次数（PRD §10.2 要求可重试） */
export const MAX_RETRIES = 3;

/** 重试退避基数（毫秒）；测试可注入更小值加速 */
export const RETRY_BACKOFF_MS = 50;

/** 同步执行器：返回成功或抛出错误；用于 process() 内部 */
export type SyncExecutor = (op: SyncOperation) => Promise<void>;

/** 重试延迟函数（默认指数退避；测试可注入） */
export type RetryDelayFn = (attempt: number) => number;

/** 默认指数退避 */
export const defaultBackoff: RetryDelayFn = (attempt) =>
  RETRY_BACKOFF_MS * Math.pow(2, attempt - 1);

/** 生成一个新 SyncOperation 记录。 */
export function makeSyncOp(
  partial: Omit<SyncOperation, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'status' | 'opStatus' | 'retries' | 'lastError' | 'nextAttemptAt'>,
): SyncOperation {
  const ts = nowIso();
  return {
    ...partial,
    id: crypto.randomUUID(),
    createdAt: ts,
    updatedAt: ts,
    version: 1,
    status: 'published',
    opStatus: 'pending',
    retries: 0,
    lastError: null,
    nextAttemptAt: null,
  };
}

/** 入队一条同步操作。 */
export async function enqueue(op: SyncOperation): Promise<string> {
  return saveSyncOperation(op);
}

/** 列出 pending 操作（按 createdAt 升序）。 */
export async function pending(): Promise<SyncOperation[]> {
  const all = await listSyncOperations();
  return all
    .filter((op) => op.opStatus === 'pending' || op.opStatus === 'failed')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** 队列状态汇总 */
export interface QueueStatus {
  pending: number;
  processing: number;
  success: number;
  failed: number;
}

export async function status(): Promise<QueueStatus> {
  const all = await listSyncOperations();
  const counts: QueueStatus = { pending: 0, processing: 0, success: 0, failed: 0 };
  for (const op of all) {
    counts[op.opStatus] += 1;
  }
  return counts;
}

/** Process options */
export interface ProcessOptions {
  /** 自定义执行器（默认 noop executor，方便单测） */
  executor?: SyncExecutor;
  /** 重试间隔函数 */
  delay?: RetryDelayFn;
  /** 是否持续消费队列；默认 true，直到 pending 为空 */
  loop?: boolean;
  /** 等待间隔（毫秒） */
  pollMs?: number;
}

/** `process` 的别名，匹配 src/components/SyncBootstrap.tsx 的 import 契约。 */
export const processSyncQueue = process;

/** 处理队列中所有 pending 操作；返回成功条数。 */
export async function process(opts: ProcessOptions = {}): Promise<number> {
  const executor = opts.executor ?? (async () => {});
  const delay = opts.delay ?? defaultBackoff;
  const loop = opts.loop ?? true;
  const pollMs = opts.pollMs ?? 0;

  let successCount = 0;
  while (true) {
    const queue = await pending();
    if (queue.length === 0) {
      if (!loop) break;
      if (pollMs <= 0) break;
      await new Promise((r) => setTimeout(r, pollMs));
      continue;
    }
    let progressed = false;
    for (const op of queue) {
      const result = await processOne(op, executor, delay);
      if (result) {
        successCount += 1;
        progressed = true;
      } else {
        progressed = true; // 也算处理了一轮（即便失败）
      }
    }
    if (!loop) break;
    if (!progressed) {
      // 没有可处理项，退出避免死循环
      break;
    }
  }
  return successCount;
}

/**
 * 处理单条操作；返回 true 表示本次执行视为"完成"（成功或重试耗尽被标记 failed）。
 */
export async function processOne(
  op: SyncOperation,
  executor: SyncExecutor = async () => {},
  delay: RetryDelayFn = defaultBackoff,
): Promise<boolean> {
  const updated: SyncOperation = {
    ...op,
    opStatus: 'processing' as SyncOperationStatus,
    updatedAt: nowIso(),
    version: op.version + 1,
  };
  await saveSyncOperation(updated);

  try {
    await executor(op);
    const success: SyncOperation = {
      ...updated,
      opStatus: 'success',
      lastError: null,
      nextAttemptAt: null,
    };
    await saveSyncOperation(success);
    // PRD §10.2 一次性消费后保留以供审计；此处不删除，调用方可显式清理
    return true;
  } catch (err) {
    const retries = op.retries + 1;
    const finalFail = retries >= MAX_RETRIES;
    const failed: SyncOperation = {
      ...updated,
      retries,
      opStatus: finalFail ? 'failed' : 'pending',
      lastError: err instanceof Error ? err.message : String(err),
      nextAttemptAt: finalFail ? null : new Date(Date.now() + delay(retries)).toISOString(),
    };
    await saveSyncOperation(failed);
    return finalFail;
  }
}

/** 手动重试：把所有 failed 标记回 pending。 */
export async function retryFailed(): Promise<number> {
  const all = await listSyncOperations();
  const failed = all.filter((op) => op.opStatus === 'failed');
  for (const op of failed) {
    const reset: SyncOperation = {
      ...op,
      opStatus: 'pending',
      retries: 0,
      lastError: null,
      nextAttemptAt: null,
      updatedAt: nowIso(),
      version: op.version + 1,
    };
    await saveSyncOperation(reset);
  }
  return failed.length;
}

/** 清理已成功的旧条目（默认保留全部；调用方可显式调用）。 */
export async function clearSuccess(): Promise<number> {
  const all = await listSyncOperations();
  let removed = 0;
  for (const op of all) {
    if (op.opStatus === 'success') {
      await removeSyncOperation(op.id);
      removed += 1;
    }
  }
  return removed;
}
